import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDateTime } from "@/lib/utils-format";
import { Send, Mail, MessageSquare, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = ["email", "sms", "whatsapp"];
const TEMPLATE_KEYS = [
  { key: "quotation_sent", label: "Quotation Sent" },
  { key: "invoice_sent", label: "Invoice Sent" },
  { key: "payment_received", label: "Payment Received" },
  { key: "followup_reminder", label: "Follow-up Reminder" },
  { key: "welcome_customer", label: "Welcome Customer" },
  { key: "custom", label: "Custom" },
];

const emptyTpl = { key: "quotation_sent", channel: "email", subject: "", body: "" };
const emptySend = { channel: "email", to: "", subject: "", body: "" };

export default function NotificationsPage() {
  const [tab, setTab] = useState("compose");
  const [logs, setLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sendForm, setSendForm] = useState(emptySend);
  const [sending, setSending] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState(emptyTpl);

  const load = async () => {
    const [l, t] = await Promise.all([api.get("/notifications"), api.get("/notifications/templates")]);
    setLogs(l.data); setTemplates(t.data);
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!sendForm.to || !sendForm.body) return toast.error("Recipient and body are required");
    setSending(true);
    try {
      await api.post("/notifications/send", sendForm);
      toast.success("Sent");
      setSendForm(emptySend);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSending(false); }
  };

  const saveTpl = async () => {
    if (!tplForm.body) return toast.error("Body required");
    try {
      await api.put("/notifications/templates", tplForm);
      toast.success("Template saved");
      setTplOpen(false); setTplForm(emptyTpl);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteTpl = async (t) => {
    if (!window.confirm(`Delete template ${t.key} (${t.channel})?`)) return;
    await api.delete(`/notifications/templates/${t.id}`);
    toast.success("Deleted");
    load();
  };

  const applyTpl = (t) => {
    setSendForm({ ...sendForm, channel: t.channel, subject: t.subject || "", body: t.body });
    setTab("compose");
    toast.success(`Template applied · ${t.key}`);
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <div className="uc-label">Engagement</div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Notifications</h1>
        <p className="text-sm text-[#5C4033] mt-1">Send emails, SMS and WhatsApp messages. Configure integration keys in <b>Settings → Integrations</b>.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F7F2E8]">
          <TabsTrigger value="compose" data-testid="notifications-tab-compose">Compose</TabsTrigger>
          <TabsTrigger value="templates" data-testid="notifications-tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="log" data-testid="notifications-tab-log">History ({logs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="pt-4">
          <div className="bg-white border border-[#EAE5D9] rounded-lg p-6 max-w-2xl space-y-3">
            <div>
              <Label>Channel</Label>
              <Select value={sendForm.channel} onValueChange={(v) => setSendForm({ ...sendForm, channel: v })}>
                <SelectTrigger data-testid="notif-channel-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {CHANNELS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Recipient {sendForm.channel === "email" ? "(email)" : "(phone in E.164, e.g. +919876543210)"}</Label>
              <Input value={sendForm.to} onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })} data-testid="notif-to-input" />
            </div>
            {sendForm.channel === "email" && (
              <div><Label>Subject</Label><Input value={sendForm.subject} onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })} data-testid="notif-subject-input" /></div>
            )}
            <div>
              <Label>Body {sendForm.channel === "email" ? "(HTML allowed)" : "(plain text)"}</Label>
              <Textarea rows={6} value={sendForm.body} onChange={(e) => setSendForm({ ...sendForm, body: e.target.value })} data-testid="notif-body-input" />
            </div>
            <Button onClick={send} disabled={sending} data-testid="notif-send-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="templates" className="pt-4">
          <div className="flex justify-end mb-3">
            <Dialog open={tplOpen} onOpenChange={setTplOpen}>
              <DialogTrigger asChild>
                <Button data-testid="notif-tpl-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">New Template</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg bg-white">
                <DialogHeader><DialogTitle className="font-[Outfit]">Template</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div>
                    <Label>Purpose</Label>
                    <Select value={tplForm.key} onValueChange={(v) => setTplForm({ ...tplForm, key: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {TEMPLATE_KEYS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Channel</Label>
                    <Select value={tplForm.channel} onValueChange={(v) => setTplForm({ ...tplForm, channel: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">{CHANNELS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {tplForm.channel === "email" && <div><Label>Subject</Label><Input value={tplForm.subject || ""} onChange={(e) => setTplForm({ ...tplForm, subject: e.target.value })} /></div>}
                  <div><Label>Body</Label><Textarea rows={6} value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })} data-testid="notif-tpl-body-input" placeholder="Use {{customer_name}} etc. as manual placeholders." /></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setTplOpen(false)}>Cancel</Button>
                  <Button onClick={saveTpl} data-testid="notif-tpl-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.map((t) => (
              <div key={t.id} className="bg-white border border-[#EAE5D9] rounded-lg p-4 space-y-2" data-testid={`notif-tpl-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-sm capitalize">{t.key.replace(/_/g, " ")}</div>
                    <Badge variant="outline" className="capitalize text-[10px] mt-1">{t.channel}</Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => applyTpl(t)} data-testid={`notif-tpl-apply-${t.id}`}>Use</Button>
                    <button onClick={() => deleteTpl(t)} className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {t.subject && <div className="text-xs text-[#5C4033]">Subject: {t.subject}</div>}
                <div className="text-xs text-[#8B7355] line-clamp-3 whitespace-pre-wrap">{t.body}</div>
              </div>
            ))}
            {templates.length === 0 && <div className="col-span-2 text-center text-[#8B7355] py-8 bg-white border border-[#EAE5D9] rounded-lg">No templates yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="log" className="pt-4">
          <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left uc-label border-b border-[#EAE5D9]">
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Channel</th>
                  <th className="px-4 py-3">To</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Subject / Preview</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`notif-log-${l.id}`}>
                    <td className="px-4 py-3 text-[#8B7355] text-xs">{fmtDateTime(l.created_at)}</td>
                    <td className="px-4 py-3 capitalize">
                      {l.channel === "email" ? <Mail className="w-3.5 h-3.5 inline mr-1" /> : <MessageSquare className="w-3.5 h-3.5 inline mr-1" />}
                      {l.channel}
                    </td>
                    <td className="px-4 py-3">{l.to}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={l.status === "sent" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}>
                        {l.status}
                      </Badge>
                      {l.error && <div className="text-xs text-red-600 mt-1">{l.error}</div>}
                    </td>
                    <td className="px-4 py-3 max-w-xs truncate">{l.subject || l.body}</td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#8B7355]">No notifications sent yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
